/**
 * The theme an interactive page wears: the interface's own tokens, resolved
 * to plain `rgba()` (the page is a separate document and cannot see the
 * app's variables, which are `oklch()`/`color-mix()` expressions), plus the
 * validated categorical chart palette as `--chart-1…--chart-8`.
 *
 * `--background` is whatever is actually painted behind the frame — the
 * thread, or the floating window's surface — so the page reads as part of
 * the reply wherever it sits.
 */
import {
  htmlPageChartVariables,
  htmlPageFallbackTheme,
  type HtmlPageTheme,
} from "@amoreg/shared/htmlPage";
import { useLayoutEffect, useState, type RefObject } from "react";

import { useTheme } from "../../hooks/useTheme";
import { resolveThemeColor } from "./resolveThemeColor";

type Mode = "light" | "dark";

/** Page variable → the interface token it mirrors. */
const TOKEN_EXPRESSIONS: ReadonlyArray<readonly [string, string]> = [
  ["--foreground", "var(--color-foreground)"],
  ["--muted", "var(--color-muted)"],
  ["--muted-foreground", "var(--color-muted-foreground)"],
  ["--card", "var(--color-card)"],
  ["--card-foreground", "var(--color-card-foreground)"],
  ["--popover", "var(--color-popover)"],
  ["--popover-foreground", "var(--color-popover-foreground)"],
  ["--secondary", "var(--color-secondary)"],
  ["--secondary-foreground", "var(--color-secondary-foreground)"],
  ["--border", "var(--color-border)"],
  ["--input", "var(--color-input)"],
  ["--ring", "var(--color-ring)"],
  ["--primary", "var(--color-primary)"],
  ["--primary-foreground", "var(--color-primary-foreground)"],
  ["--accent", "var(--color-accent)"],
  ["--accent-foreground", "var(--color-accent-foreground)"],
  ["--destructive", "var(--color-destructive)"],
  ["--warning", "var(--color-warning)"],
  ["--success", "var(--color-success)"],
  ["--info", "var(--color-info)"],
];

const TRANSPARENT = /^(?:transparent|rgba\(\s*0,\s*0,\s*0,\s*0\s*\))$/;

/** The first painted background at or above `host`, as a CSS colour. */
function paintedBackground(host: HTMLElement): string | null {
  for (let element: HTMLElement | null = host; element; element = element.parentElement) {
    const color = getComputedStyle(element).backgroundColor;
    if (color && !TRANSPARENT.test(color.replace(/\s+/g, " ").trim())) return color;
  }
  return null;
}

/** Reads the page theme as the interface paints it around `host` right now. */
export function readHtmlPageTheme(host: HTMLElement, mode: Mode): HtmlPageTheme {
  const fallback = htmlPageFallbackTheme(mode).variables;
  const variables: Record<string, string> = {};
  const background = paintedBackground(host);
  variables["--background"] = resolveThemeColor(
    host,
    background ?? "var(--color-background)",
    fallback["--background"]!,
  );
  for (const [name, expression] of TOKEN_EXPRESSIONS) {
    variables[name] = resolveThemeColor(host, expression, fallback[name]!);
  }
  Object.assign(variables, htmlPageChartVariables(mode));
  const root = getComputedStyle(document.documentElement);
  variables["--radius"] = root.getPropertyValue("--radius").trim() || fallback["--radius"]!;
  variables["--font-sans"] =
    root.getPropertyValue("--font-sans").trim() || fallback["--font-sans"]!;
  variables["--font-mono"] =
    root.getPropertyValue("--font-mono").trim() || fallback["--font-mono"]!;
  return { appearance: mode, variables };
}

function sameTheme(left: HtmlPageTheme | null, right: HtmlPageTheme): boolean {
  if (left === null || left.appearance !== right.appearance) return false;
  const names = Object.keys(right.variables);
  return (
    names.length === Object.keys(left.variables).length &&
    names.every((name) => left.variables[name] === right.variables[name])
  );
}

/**
 * The page theme around `hostRef`, re-read when the mode or the active theme
 * changes and when the root element's class or style changes (custom
 * palettes and fonts are applied there). Stable while nothing changed, so a
 * consumer can post it only on real changes. `null` until first measured.
 */
export function useHtmlPageTheme(hostRef: RefObject<HTMLElement | null>): HtmlPageTheme | null {
  const { theme: themeId, resolvedTheme } = useTheme();
  const [theme, setTheme] = useState<HtmlPageTheme | null>(null);
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => {
      const next = readHtmlPageTheme(host, resolvedTheme);
      setTheme((current) => (sameTheme(current, next) ? current : next));
    };
    measure();
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(measure);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style", "data-theme"],
    });
    return () => observer.disconnect();
  }, [hostRef, resolvedTheme, themeId]);
  return theme;
}
